import type {
  AssistantPresentationChecklistItem,
  AssistantPresentationArtifactItem,
  AssistantPresentationDetailRow,
  AssistantPresentationDiagnostics,
  AssistantPresentationMetricItem,
  AssistantPresentationModel,
  AssistantPresentationRelatedLinkGroup,
  AssistantPresentationRelatedLinkItem,
  AssistantPresentationResultCard,
  AssistantPresentationSection,
  AssistantPresentationSourceItem,
  AssistantPresentationTaskProposal,
  AssistantPresentationTaskThread,
  AssistantResponseKind,
} from "@triedge/ai-assistant-ui";
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantPresentationDiagnostics,
  AdmissionsAssistantTaskProposalCard,
  AdmissionsAssistantTaskThreadRollup,
  AdmissionsAssistantTaskThreadStatus,
  AdmissionsCitation,
} from "./types";
import type {
  AssistantLinkGroup,
  AssistantResultCard,
  AssistantSourceRow,
} from "./assistantResponsePresentation";
import type { AssistantSettingsSpecialistSnapshot } from "./assistantSettingsSpecialist";

const toRelatedLinkItem = (
  groupId: string,
  item: AssistantLinkGroup["items"][number],
  index: number,
): AssistantPresentationRelatedLinkItem => ({
  id: item.id || `${groupId}-${index + 1}`,
  href: item.href,
  label: item.label,
  meta: item.meta,
  preview: item.preview,
  title: item.title,
  external: item.external,
});

const toRelatedLinkGroup = (
  group: AssistantLinkGroup,
): AssistantPresentationRelatedLinkGroup => ({
  id: group.key,
  label: group.label,
  items: group.items.map((item, index) =>
    toRelatedLinkItem(group.key, item, index),
  ),
});

const toSourceItem = (
  source: AssistantSourceRow,
): AssistantPresentationSourceItem => ({
  id: source.id,
  label: source.label,
  meta: [source.table, source.fieldPath].filter(Boolean).join(" • "),
  preview: source.preview,
  title: source.title,
});

const toResultCardDetailRows = (
  card: AssistantResultCard,
): AssistantPresentationDetailRow[] => {
  const rows: AssistantPresentationDetailRow[] = [];
  if (card.meta) {
    rows.push({ label: "Status", value: card.meta });
  }
  if (card.preview) {
    rows.push({ label: "Summary", value: card.preview });
  }
  return rows;
};

const toResultCard = (
  card: AssistantResultCard,
): AssistantPresentationResultCard => ({
  id: card.id,
  label: card.label,
  meta: card.meta,
  preview: card.preview,
  primaryAction: card.primaryAction
    ? {
        label: card.primaryAction.label,
        href: card.primaryAction.href,
      }
    : null,
  secondaryAction: card.secondaryAction
    ? {
        label: card.secondaryAction.label,
        href: card.secondaryAction.href,
      }
    : null,
  detailRows: toResultCardDetailRows(card),
});

const buildSettingsOwnerRouteCards = (
  snapshot: AssistantSettingsSpecialistSnapshot | null | undefined,
): AssistantPresentationResultCard[] => {
  if (!snapshot || snapshot.ownerRoutes.length === 0) {
    return [];
  }
  return snapshot.ownerRoutes.slice(0, 3).map((route, index) => ({
    id: `settings-owner-route-${index + 1}`,
    label: route.label,
    eyebrow: "Settings owner",
    badge: "Route",
    meta: snapshot.routeLabel,
    preview: route.summary,
    primaryAction: {
      label: "Open owner route",
      href: route.href,
    },
    detailRows: [{ label: "Summary", value: route.summary }],
  }));
};

const toArtifactItems = (
  artifactPreview: AdmissionsAssistantArtifactPreview | null,
): AssistantPresentationArtifactItem[] => {
  if (!artifactPreview) {
    return [];
  }
  const items: AssistantPresentationArtifactItem[] = [];
  if (artifactPreview.workbookArtifact) {
    items.push({
      id: artifactPreview.workbookArtifact.id,
      label: artifactPreview.workbookArtifact.label,
      kind: artifactPreview.workbookArtifact.artifactType,
      meta: artifactPreview.workbookArtifact.fileName ?? null,
      preview:
        artifactPreview.workbookPreview?.title ??
        "Workbook is ready in the assistant workspace.",
      href: artifactPreview.workbookArtifact.downloadUrl ?? null,
      sourceRef: artifactPreview.workbookArtifact.sourceRef ?? null,
      actionLabel: "Open workspace",
      badge: "Workbook",
    });
  }
  if (artifactPreview.spreadsheetDownloadArtifact) {
    items.push({
      id: artifactPreview.spreadsheetDownloadArtifact.id,
      label: artifactPreview.spreadsheetDownloadArtifact.label,
      kind: artifactPreview.spreadsheetDownloadArtifact.artifactType,
      meta: artifactPreview.spreadsheetDownloadArtifact.fileName ?? null,
      preview: "Spreadsheet export is ready to download.",
      href: artifactPreview.spreadsheetDownloadArtifact.downloadUrl ?? null,
      sourceRef:
        artifactPreview.workbookArtifact?.sourceRef ??
        artifactPreview.spreadsheetDownloadArtifact.sourceRef ??
        null,
      actionLabel: "Download export",
      badge: "Spreadsheet",
    });
  }
  if (artifactPreview.chartArtifact) {
    items.push({
      id: artifactPreview.chartArtifact.id,
      label: artifactPreview.chartArtifact.label,
      kind: artifactPreview.chartArtifact.artifactType,
      meta: artifactPreview.chartArtifact.fileName ?? null,
      preview:
        artifactPreview.chartPanels?.[0]?.title ??
        "Chart is ready in the assistant response.",
      href: artifactPreview.chartArtifact.downloadUrl ?? null,
      sourceRef: artifactPreview.chartArtifact.sourceRef ?? null,
      actionLabel: "Download chart artifact",
      badge: "Chart",
    });
  }
  if (artifactPreview.visualization3DArtifact) {
    items.push({
      id: artifactPreview.visualization3DArtifact.id,
      label: artifactPreview.visualization3DArtifact.label,
      kind: artifactPreview.visualization3DArtifact.artifactType,
      meta: artifactPreview.visualization3DArtifact.fileName ?? null,
      preview:
        artifactPreview.visualization3DSpace?.title ??
        "3D visualization is ready in the assistant workspace.",
      href: artifactPreview.visualization3DArtifact.downloadUrl ?? null,
      sourceRef: artifactPreview.visualization3DArtifact.sourceRef ?? null,
      actionLabel: "Open 3D workspace",
      badge: "3D Visualization",
    });
  }
  if (artifactPreview.landingPageArtifact) {
    items.push({
      id: artifactPreview.landingPageArtifact.id,
      label: artifactPreview.landingPageArtifact.label,
      kind: artifactPreview.landingPageArtifact.artifactType,
      meta: artifactPreview.landingPageArtifact.fileName ?? null,
      preview:
        artifactPreview.landingPagePreview?.title ??
        "Landing page preview is ready in this thread.",
      href: artifactPreview.landingPageArtifact.downloadUrl ?? null,
      sourceRef: artifactPreview.landingPageArtifact.sourceRef ?? null,
      actionLabel: "Open landing page",
      badge: "Landing page",
    });
  }
  if (artifactPreview.diagramArtifact) {
    items.push({
      id: artifactPreview.diagramArtifact.id,
      label: artifactPreview.diagramArtifact.label,
      kind: artifactPreview.diagramArtifact.artifactType,
      meta: artifactPreview.diagramArtifact.fileName ?? null,
      preview:
        artifactPreview.diagramPreview?.title ??
        "Diagram preview is ready in this thread.",
      href: artifactPreview.diagramArtifact.downloadUrl ?? null,
      sourceRef: artifactPreview.diagramArtifact.sourceRef ?? null,
      actionLabel: "Open diagram artifact",
      badge: "Diagram",
    });
  }
  for (const artifact of artifactPreview.artifactCards ?? []) {
    items.push({
      id: artifact.id,
      label: artifact.label,
      kind: artifact.artifactType,
      meta: artifact.fileName ?? null,
      preview: "Artifact is ready to open or download.",
      href: artifact.downloadUrl ?? null,
      sourceRef: artifact.sourceRef ?? null,
      actionLabel: "Open artifact",
      badge: artifact.artifactType,
    });
  }
  return items;
};

const toTaskProposal = (
  proposal: AdmissionsAssistantTaskProposalCard | null,
): AssistantPresentationTaskProposal | null => {
  if (!proposal) {
    return null;
  }
  return {
    proposalId: proposal.proposalId,
    title: proposal.title,
    summary: proposal.summary,
    question: proposal.question,
    startLabel: "Start task",
    askInChatLabel: "Ask in chat instead",
    dismissLabel: "Dismiss",
  };
};

const buildTaskThreadDetailsSummary = (
  mainText: string,
  rollup: AdmissionsAssistantTaskThreadRollup | null,
): string | null => {
  const summary = String(mainText ?? "").trim();
  if (summary) {
    return summary;
  }
  const firstRollupSummary =
    rollup?.events.find((event) => String(event.summary ?? "").trim())
      ?.summary ?? null;
  return firstRollupSummary ? String(firstRollupSummary).trim() : null;
};

const toTaskThread = (
  taskThread: AdmissionsAssistantTaskThreadStatus | null,
  rollup: AdmissionsAssistantTaskThreadRollup | null,
  detailsSummary: string | null,
  degradedReason: string | null,
): AssistantPresentationTaskThread | null => {
  if (!taskThread) {
    return null;
  }
  return {
    threadId: taskThread.threadId,
    title: taskThread.title,
    state: taskThread.state,
    stateLabel: taskThread.stateLabel,
    statusMessage: taskThread.statusMessage ?? null,
    artifactCount: taskThread.artifactCount ?? null,
    progressPercent: taskThread.progressPercent ?? null,
    detailsSummary,
    degradedReason,
    events:
      rollup?.events.map((event, index) => ({
        id: event.messageId ?? `${taskThread.threadId}-${index + 1}`,
        label: event.label,
        state: event.state ?? null,
        summary: event.summary ?? null,
        content: event.content ?? null,
        artifactCount: event.artifactCount ?? null,
      })) ?? [],
  };
};

const toDiagnostics = (
  diagnostics: AdmissionsAssistantPresentationDiagnostics | null,
  explanationText: string | null,
): AssistantPresentationDiagnostics | null => {
  if (!diagnostics && !explanationText) {
    return null;
  }
  return {
    title: diagnostics?.title ?? "Diagnostics",
    summary:
      diagnostics?.summary ??
      "Additional explanation is available for this assistant response.",
    explanation: explanationText ?? null,
    operatorGuidance: diagnostics?.operatorGuidance ?? null,
    targetRoute: diagnostics?.targetRoute ?? null,
    defaultOpen: false,
    displayMode: diagnostics?.displayMode ?? "drawer",
  };
};

const inferResponseKind = (input: {
  mainText: string;
  artifactPreview: AdmissionsAssistantArtifactPreview | null;
  taskProposal: AdmissionsAssistantTaskProposalCard | null;
  taskThread: AdmissionsAssistantTaskThreadStatus | null;
  projectedSections?: AssistantPresentationSection[];
  settingsSpecialistSnapshot?: AssistantSettingsSpecialistSnapshot | null;
  resultCards: AssistantResultCard[];
  relatedLinkGroups: AssistantLinkGroup[];
  readOnlyDegradedReason: string | null;
}): AssistantResponseKind => {
  const artifactPreview = input.artifactPreview;
  const artifactRefs = [
    artifactPreview?.workbookArtifact,
    artifactPreview?.spreadsheetDownloadArtifact,
    artifactPreview?.chartArtifact,
    artifactPreview?.visualization3DArtifact,
    artifactPreview?.landingPageArtifact,
    artifactPreview?.diagramArtifact,
    ...(artifactPreview?.artifactCards ?? []),
  ].filter(Boolean);
  const artifactText = artifactRefs
    .map(
      (artifact) =>
        `${artifact?.artifactType ?? ""} ${artifact?.mimeType ?? ""} ${artifact?.fileName ?? ""}`,
    )
    .join(" ")
    .toLowerCase();
  const mainText = String(input.mainText ?? "");
  const promptText = mainText.toLowerCase();

  if (
    artifactPreview?.chartArtifact ||
    input.projectedSections?.some((section) => section.type === "chart")
  ) {
    return "chart_artifact";
  }
  if (
    artifactPreview?.visualization3DArtifact ||
    artifactPreview?.visualization3DSpace
  ) {
    return "visualization3d_artifact";
  }
  if (artifactPreview?.landingPageArtifact) {
    return "landing_page_artifact";
  }
  if (artifactPreview?.diagramArtifact) {
    return "diagram_artifact";
  }
  if (/pdf|application\/pdf/.test(artifactText)) {
    return "pdf_generation";
  }
  if (/xlsx|spreadsheetml|excel/.test(artifactText)) {
    return "xlsx_export";
  }
  if (
    artifactPreview?.workbookArtifact ||
    artifactPreview?.spreadsheetDownloadArtifact
  ) {
    return "spreadsheet_artifact";
  }
  if (
    input.readOnlyDegradedReason ||
    /\b(could not load|not available|unable to load|not enough data)\b/i.test(
      mainText,
    )
  ) {
    return "data_unavailable";
  }
  if (input.taskProposal || input.taskThread) {
    return "proactive_guide";
  }
  if (
    input.settingsSpecialistSnapshot?.checklistItems.length ||
    /\b(todo|to-do|next steps|checklist|priorit|readiness)/.test(promptText)
  ) {
    return "todo_list";
  }
  if (input.resultCards.length > 0) {
    return "navigation_assistance";
  }
  if (/\b(draft|message|email|letter)\b/.test(promptText)) {
    return "draft_creator";
  }
  return "text_summary";
};

const projectChecklistFromText = (
  text: string,
): {
  summaryText: string;
  checklistItems: AssistantPresentationChecklistItem[];
} | null => {
  const normalized = String(text ?? "")
    .replace(/([a-z)])(\d+\.)/g, "$1\n$2")
    .replace(/\s+(\d+\.)\s+/g, "\n$1 ")
    .trim();
  const matches = Array.from(
    normalized.matchAll(/(?:^|\n)\s*(\d+)\.\s+([^\n]+?)(?=\n\s*\d+\.|\s*$)/g),
  );
  if (matches.length < 1) {
    if (
      !/\b(?:priorit|readiness|next steps?|action items?|risk)\b/i.test(
        normalized,
      )
    ) {
      return null;
    }
    const candidates = normalized
      .replace(/^#+\s*/gm, "")
      .split(/(?:[.!?]\s+|\n+)/)
      .map((entry) => entry.trim().replace(/[:;,\s]+$/, ""))
      .filter(
        (entry) =>
          entry.length >= 24 &&
          !/^(sources?|actions?|current workspace)$/i.test(entry),
      )
      .slice(0, 5);
    if (candidates.length < 1) {
      return null;
    }
    return {
      summaryText: "Recommended next checks:",
      checklistItems: candidates.map((label, index) => ({
        id: `projected-check-${index + 1}`,
        label,
        detail: null,
        status: "todo",
      })),
    };
  }
  const firstIndex = matches[0]?.index ?? 0;
  const summaryText = normalized.slice(0, firstIndex).trim();
  const checklistItems = matches.map((match, index) => {
    const rawItem = String(match[2] ?? "").trim();
    const [label, ...detailParts] = rawItem.split(/\s+[—-]\s+/);
    return {
      id: `projected-check-${index + 1}`,
      label: label.trim(),
      detail: detailParts.join(" - ").trim() || null,
      status: "todo",
    } satisfies AssistantPresentationChecklistItem;
  });
  return {
    summaryText: summaryText || "Recommended next checks:",
    checklistItems,
  };
};

export const buildAdmissionsAssistantPresentationModel = (input: {
  module: string;
  routeFamily: string;
  mainText: string;
  explanationText: string | null;
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
  sources: AssistantSourceRow[];
  relatedLinkGroups: AssistantLinkGroup[];
  resultCards: AssistantResultCard[];
  artifactPreview: AdmissionsAssistantArtifactPreview | null;
  taskProposal: AdmissionsAssistantTaskProposalCard | null;
  taskThread: AdmissionsAssistantTaskThreadStatus | null;
  taskThreadRollup: AdmissionsAssistantTaskThreadRollup | null;
  presentationDiagnostics: AdmissionsAssistantPresentationDiagnostics | null;
  readOnlyDegradedReason: string | null;
  settingsSpecialistSnapshot?: AssistantSettingsSpecialistSnapshot | null;
  projectedSections?: AssistantPresentationSection[];
  suppressRelatedLinks?: boolean;
  responseKind?: AssistantResponseKind | null;
}): AssistantPresentationModel => {
  const sections: AssistantPresentationModel["sections"] = [];
  const mainText = String(input.mainText ?? "").trim();
  const responseKind =
    input.responseKind ??
    inferResponseKind({
      mainText,
      artifactPreview: input.artifactPreview,
      taskProposal: input.taskProposal,
      taskThread: input.taskThread,
      projectedSections: input.projectedSections,
      settingsSpecialistSnapshot: input.settingsSpecialistSnapshot,
      resultCards: input.resultCards,
      relatedLinkGroups: input.relatedLinkGroups,
      readOnlyDegradedReason: input.readOnlyDegradedReason,
    });
  const allowTextChecklistProjection =
    input.module !== "students-families" &&
    input.routeFamily !== "students-families";
  const projectedChecklist =
    allowTextChecklistProjection &&
    (responseKind === "todo_list" || responseKind === "proactive_guide")
      ? projectChecklistFromText(mainText)
      : null;
  const summaryText = projectedChecklist?.summaryText ?? mainText;
  if (summaryText) {
    sections.push({
      type: "summary",
      text: summaryText,
    });
  }

  if (input.settingsSpecialistSnapshot?.metricItems.length) {
    sections.push({
      type: "metric_grid",
      title: "Settings snapshot",
      items: input.settingsSpecialistSnapshot
        .metricItems as AssistantPresentationMetricItem[],
    });
  }

  if (input.settingsSpecialistSnapshot?.checklistItems.length) {
    sections.push({
      type: "checklist",
      title: "Useful next checks",
      items: input.settingsSpecialistSnapshot
        .checklistItems as AssistantPresentationChecklistItem[],
    });
  }

  if (projectedChecklist?.checklistItems.length) {
    sections.push({
      type: "checklist",
      title:
        responseKind === "todo_list"
          ? "Recommended priorities"
          : "Recommended next checks",
      items: projectedChecklist.checklistItems,
    });
  }

  if (input.resultCards.length > 0) {
    sections.push({
      type: "result_cards",
      title: "Applications needing action",
      cards: input.resultCards.map((card) => toResultCard(card)),
    });
  } else {
    const settingsOwnerCards = buildSettingsOwnerRouteCards(
      input.settingsSpecialistSnapshot,
    );
    if (settingsOwnerCards.length > 0) {
      sections.push({
        type: "result_cards",
        title: "Settings owner routes",
        cards: settingsOwnerCards,
      });
    }
  }

  if (input.projectedSections?.length) {
    sections.push(...input.projectedSections);
  }

  const artifactItems = toArtifactItems(input.artifactPreview);
  if (artifactItems.length > 0) {
    sections.push({
      type: "artifacts",
      title: "Artifacts & workspaces",
      items: artifactItems,
    });
  }

  const proposal = toTaskProposal(input.taskProposal);
  if (proposal) {
    sections.push({
      type: "task_proposal",
      proposal,
    });
  }

  const taskThreadDetailsSummary = buildTaskThreadDetailsSummary(
    mainText,
    input.taskThreadRollup,
  );
  const taskThread = toTaskThread(
    input.taskThread,
    input.taskThreadRollup,
    taskThreadDetailsSummary,
    input.readOnlyDegradedReason,
  );
  if (taskThread) {
    sections.push({
      type: "task_thread",
      thread: taskThread,
    });
  }

  const sourceItems = input.sources.map((source) => toSourceItem(source));
  if (sourceItems.length > 0) {
    sections.push({
      type: "sources",
      items: sourceItems,
    });
  }

  if (
    !input.suppressRelatedLinks &&
    input.resultCards.length === 0 &&
    input.relatedLinkGroups.length > 0
  ) {
    sections.push({
      type: "related_links",
      groups: input.relatedLinkGroups.map((group) => toRelatedLinkGroup(group)),
    });
  }

  const diagnostics = toDiagnostics(
    input.presentationDiagnostics,
    input.explanationText,
  );
  if (diagnostics) {
    sections.push({
      type: "diagnostics",
      diagnostics,
    });
  }

  return {
    version: "1.0.0",
    module: input.module,
    routeFamily: input.routeFamily,
    responseKind,
    sections,
  };
};
